// ---------------------------------------------------------------------------
// R12-2 — THE EDIT DOOR FOR SOMEBODY IN HER CIRCLE.
//
// **EXTRACTED SO A GATE CAN IMPORT IT** (R8's own ledger currency: the thing that had to move is the
// thing nothing could reach). Inside an eleven-thousand-line shell this form was reachable by a regex
// and nothing else — and it is the surface that carries `personToChart`, whose 12-hour/24-hour trap
// this house has already paid for once (a chart cast twelve hours out, rendering perfectly). It holds
// three hooks, so it is the `TierCards` / `MicButton` / `SignInModal` class rather than a hookless
// leaf a gate CALLS; **the reach it buys is the IMPORT.**
//
// Moved VERBATIM — not one member-visible line changed.
// ---------------------------------------------------------------------------
import { useState, type FormEvent } from "react";
import { CreateRoomForm } from "./CreateRoomForm";
import { ModalBackdrop } from "./ModalBackdrop";
import { t } from "../strings.js";
import { DEFAULT_LANGUAGE } from "../language.js";
import { combineName } from "../nameParts.js";
import { editPerson, personNote } from "../lib/people";
import { personToChart, type BirthChartForm } from "./BirthFields";
import { type SubjectPickerPerson } from "./DuplicatePersonPrompt";

export function PersonEditModal({ person, onClose, onSaved, language = DEFAULT_LANGUAGE }: { person: SubjectPickerPerson; onClose: () => void; onSaved: () => void; language?: string }) {
  const [form, setForm] = useState<BirthChartForm>(() => personToChart(person));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const update = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) => setForm((f) => ({ ...f, [field]: value }));
  async function submit(event: FormEvent) {
    event.preventDefault();
    const name = combineName(form.firstName, form.middleName, form.lastName) || form.name;
    if (!name.trim()) { setErr(t(language, "subject.editNameRequired")); return; }
    setSaving(true); setErr("");
    try {
      const answer = await editPerson<SubjectPickerPerson>(person.id, { ...form, name });
      if (!answer.ok) {
        setErr(personNote(answer, { signIn: t(language, "subject.editSignIn"), failed: t(language, "common.saveFailed") }));
        setSaving(false);
        return;
      }
      onSaved();
    } catch { setErr(t(language, "common.saveFailed")); setSaving(false); }
  }
  const heading = t(language, "subject.edit", { name: person.name });
  return (
    <ModalBackdrop label={heading} className="person-edit-modal">
      <div className="person-edit">
        <div className="person-edit__head">
          <h2>{heading}</h2>
          <button type="button" className="person-edit__close" onClick={onClose} aria-label={t(language, "common.close")}>×</button>
        </div>
        <CreateRoomForm birthChart={form} updateBirthChart={update} onSubmit={submit} language={language}
          submitLabel={saving ? t(language, "result.saving") : t(language, "subject.editSave")}
          nameHint={t(language, "subject.editHint")} generating={saving} />
        {err ? <p className="person-edit__err">{err}</p> : null}
      </div>
    </ModalBackdrop>
  );
}
